import { supabase } from '@/lib/supabase'
import type {
  HangarRow,
  HangarInsert,
  HangarUpdate,
  HangarTableRow,
  HangarFilters,
} from '@/types/hangars'
import type { AreaPlacementFieldsInput } from '@/types/area-placement'

/**
 * Area auto-placement column keys (Event Zone Editor). Shared by create/update
 * so the placement settings flow through without enumerating each field twice.
 */
const AREA_PLACEMENT_KEYS: (keyof AreaPlacementFieldsInput)[] = [
  'area_category',
  'turnover_mode',
  'parking_mode',
  'tow_min_wingspan_ft',
  'auto_placement',
  'fill_priority',
  'pattern',
  'active_from',
  'active_until',
  'release_from',
  'release_until',
  'placement_heading',
  'progression',
  'lanes',
  'coupling',
  'pitch_mode',
  'safety_margin_ft',
  'auto_sort',
  'taxiway_exit_point_id',
  'fallback_area_id',
  'exit_heading',
  'exit_throughput',
  'eligibility',
  'placement_config',
]

/** Copy only the defined placement fields from a payload into a plain object. */
function pickPlacementFields(
  source: AreaPlacementFieldsInput
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const key of AREA_PLACEMENT_KEYS) {
    const value = source[key]
    if (value !== undefined) out[key] = value
  }
  return out
}

/**
 * Fetch all hangars with optional filtering
 * Can filter by location if locationId is provided
 * Includes unit_count for T-hangars
 */
export async function fetchHangars(
  organizationId: string,
  filters?: HangarFilters
): Promise<HangarTableRow[]> {
  let query = supabase
    .from('areas')
    .select(
      `id, name, area_code, type, exterior_width, exterior_depth, exterior_height, total_footprint_sqft, status, location_id, created_at,
       latitude, longitude, altitude, rotation, settings,
       units(count)`
    )
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .order('name')

  // Apply filters
  if (filters?.locationId) {
    query = query.eq('location_id', filters.locationId)
  }

  if (filters?.search) {
    query = query.or(
      `name.ilike.%${filters.search}%,area_code.ilike.%${filters.search}%`
    )
  }

  if (filters?.type) {
    query = query.eq('type', filters.type)
  }

  if (filters?.status) {
    query = query.eq('status', filters.status)
  }

  const { data, error } = await query

  if (error) throw error

  // Transform result to flatten unit count
  return (data || []).map((row) => {
    const { units, ...rest } = row as Record<string, unknown>
    const unitCount = Array.isArray(units)
      ? (units[0] as { count: number })?.count ?? 0
      : 0
    return {
      ...rest,
      unit_count: unitCount,
    } as HangarTableRow
  })
}

/**
 * Fetch a single building by ID
 */
export async function fetchHangar(id: string): Promise<HangarRow> {
  const { data, error } = await supabase
    .from('areas')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data as HangarRow
}

/**
 * Create a new building
 */
export async function createHangar(data: HangarInsert): Promise<HangarRow> {
  const { data: created, error } = await supabase
    .from('areas')
    .insert([{
      organization_id: data.organization_id,
      location_id: data.location_id,
      name: data.name,
      area_code: data.area_code.toUpperCase(),
      type: data.type,
      exterior_width: data.exterior_width,
      exterior_depth: data.exterior_depth,
      exterior_height: data.exterior_height,
      latitude: data.latitude,
      longitude: data.longitude,
      altitude: data.altitude ?? null,
      rotation: data.rotation ?? 0,
      door_width: data.door_width ?? null,
      door_height: data.door_height ?? null,
      // Building features
      construction_year: data.construction_year ?? null,
      fire_suppression: data.fire_suppression ?? null,
      security_system: data.security_system ?? null,
      // Pricing
      pricing_model: data.pricing_model ?? 'fixed',
      monthly_rate_private: data.monthly_rate_private ?? null,
      monthly_rate_shared: data.monthly_rate_shared ?? null,
      transient_rate_nightly: data.transient_rate_nightly ?? null,
      rate_per_sqft_private: data.rate_per_sqft_private ?? null,
      rate_per_sqft_shared: data.rate_per_sqft_shared ?? null,
      status: data.status ?? 'active',
      type_config: data.type_config ?? {},
      settings: data.settings ?? {},
      // Event auto-placement settings (area level)
      ...pickPlacementFields(data),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any])
    .select()
    .single()

  if (error) throw error
  return created as HangarRow
}

/**
 * Update an existing building
 */
export async function updateHangar(
  id: string,
  data: HangarUpdate
): Promise<HangarRow> {
  const updateData: Record<string, unknown> = {}

  // Basic fields
  if (data.name !== undefined) updateData.name = data.name
  if (data.area_code !== undefined)
    updateData.area_code = data.area_code.toUpperCase()
  if (data.type !== undefined) updateData.type = data.type

  // Dimensions
  if (data.exterior_width !== undefined) updateData.exterior_width = data.exterior_width
  if (data.exterior_depth !== undefined) updateData.exterior_depth = data.exterior_depth
  if (data.exterior_height !== undefined) updateData.exterior_height = data.exterior_height

  // Geographic
  if (data.latitude !== undefined) updateData.latitude = data.latitude
  if (data.longitude !== undefined) updateData.longitude = data.longitude
  if (data.altitude !== undefined) updateData.altitude = data.altitude
  if (data.rotation !== undefined) updateData.rotation = data.rotation

  // Door configuration
  if (data.door_width !== undefined) updateData.door_width = data.door_width
  if (data.door_height !== undefined) updateData.door_height = data.door_height

  // Building features
  if (data.construction_year !== undefined) updateData.construction_year = data.construction_year
  if (data.fire_suppression !== undefined) updateData.fire_suppression = data.fire_suppression
  if (data.security_system !== undefined) updateData.security_system = data.security_system

  // Pricing
  if (data.pricing_model !== undefined)
    updateData.pricing_model = data.pricing_model
  if (data.monthly_rate_private !== undefined)
    updateData.monthly_rate_private = data.monthly_rate_private
  if (data.monthly_rate_shared !== undefined)
    updateData.monthly_rate_shared = data.monthly_rate_shared
  if (data.transient_rate_nightly !== undefined)
    updateData.transient_rate_nightly = data.transient_rate_nightly
  if (data.rate_per_sqft_private !== undefined)
    updateData.rate_per_sqft_private = data.rate_per_sqft_private
  if (data.rate_per_sqft_shared !== undefined)
    updateData.rate_per_sqft_shared = data.rate_per_sqft_shared

  // Status and config
  if (data.status !== undefined) updateData.status = data.status
  if (data.type_config !== undefined) updateData.type_config = data.type_config
  if (data.settings !== undefined) updateData.settings = data.settings

  // Event auto-placement settings (area level)
  Object.assign(updateData, pickPlacementFields(data))

  const { data: updated, error } = await supabase
    .from('areas')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return updated as HangarRow
}

/**
 * Soft delete an area and all its child units atomically
 * Uses database function to cascade soft-delete to units and clean up FK references
 * Unit codes are permanently reserved (ERP standard - no code reuse)
 */
export async function deleteArea(id: string): Promise<{ units_deleted: number }> {
  const { data, error } = await supabase
    .rpc('soft_delete_area', { p_area_id: id })

  if (error) throw error
  return data as { units_deleted: number }
}

/**
 * Building with its first unit - for building editor
 */
export interface BuildingWithUnit {
  building: HangarRow
  unit: {
    id: string
    unit_code: string
    name: string | null
    door_width: number | null
    door_height: number | null
    door_type: string | null
    interior_width: number | null
    interior_depth: number | null
    interior_height: number | null
  } | null
}

/**
 * Building with all its units - for building editor (multi-unit templates)
 */
export interface BuildingWithAllUnits {
  building: HangarRow
  units: Array<{
    id: string
    unit_code: string
    name: string | null
    door_width: number | null
    door_height: number | null
    door_type: string | null
    interior_width: number | null
    interior_depth: number | null
    interior_height: number | null
    offset_x: number | null
    offset_y: number | null
    unit_rotation: number | null
  }>
}

/**
 * Fetch a building with ALL its units for the building editor
 * Used when editing multi-unit buildings (T-hangars, single-row, etc.)
 * to reconstruct per-unit overrides from unit records
 */
export async function fetchBuildingWithAllUnits(id: string): Promise<BuildingWithAllUnits> {
  const { data: building, error: buildingError } = await supabase
    .from('areas')
    .select('*')
    .eq('id', id)
    .single()

  if (buildingError) throw buildingError

  const { data: units, error: unitsError } = await supabase
    .from('units')
    .select('id, unit_code, name, door_width, door_height, door_type, interior_width, interior_depth, interior_height, offset_x, offset_y, unit_rotation')
    .eq('area_id', id)
    .is('deleted_at', null)
    .order('sequence')

  if (unitsError) throw unitsError

  return {
    building: building as HangarRow,
    units: units || [],
  }
}

/**
 * Fetch a building with its first unit for the building editor
 * Used when editing an existing building
 */
export async function fetchBuildingWithUnit(id: string): Promise<BuildingWithUnit> {
  // Fetch building
  const { data: building, error: buildingError } = await supabase
    .from('areas')
    .select('*')
    .eq('id', id)
    .single()

  if (buildingError) throw buildingError

  // Fetch first unit for this building
  const { data: units, error: unitsError } = await supabase
    .from('units')
    .select('id, unit_code, name, door_width, door_height, door_type, interior_width, interior_depth, interior_height')
    .eq('area_id', id)
    .is('deleted_at', null)
    .order('sequence')
    .limit(1)

  if (unitsError) throw unitsError

  return {
    building: building as HangarRow,
    unit: units && units.length > 0 ? units[0] : null,
  }
}

/**
 * Delete all units for a building (hard delete)
 * Used when updating multi-unit buildings to replace units
 *
 * NOTE: We use hard delete here because the unique constraint on
 * (building_id, unit_code) doesn't account for deleted_at, so
 * soft-deleted units would conflict with newly created ones.
 */
export async function deleteHangarUnits(areaId: string): Promise<void> {
  const { error } = await supabase
    .from('units')
    .delete()
    .eq('area_id', areaId)

  if (error) throw error
}

// ============================================
// 3D RENDERING DATA
// ============================================

/**
 * Unit data for 3D rendering (minimal fields needed for rendering)
 */
export interface UnitFor3D {
  id: string
  unit_code: string
  type: string | null
  interior_width: number | null
  interior_depth: number | null
  interior_height: number | null
  door_width: number | null
  door_height: number | null
  door_type: string | null
  offset_x: number | null
  offset_y: number | null
  unit_rotation: number | null
  parent_unit_id: string | null
}

/**
 * Building with units for 3D rendering
 */
export interface HangarWith3DData extends HangarRow {
  units: UnitFor3D[]
}

/**
 * Fetch all hangars for a location WITH their units for 3D rendering
 * This is optimized for map rendering - includes all units needed for template-aware rendering
 */
export async function fetchHangarsWithUnitsForLocation(
  locationId: string
): Promise<HangarWith3DData[]> {
  // Fetch buildings for location
  const { data: hangars, error: hangarsError } = await supabase
    .from('areas')
    .select('*')
    .eq('location_id', locationId)
    .is('deleted_at', null)
    .order('name')

  if (hangarsError) throw hangarsError
  if (!hangars || hangars.length === 0) return []

  // Fetch all units for these buildings in one query
  const buildingIds = hangars.map(h => h.id)
  const { data: units, error: unitsError } = await supabase
    .from('units')
    .select('id, unit_code, type, interior_width, interior_depth, interior_height, door_width, door_height, door_type, offset_x, offset_y, unit_rotation, area_id, parent_unit_id')
    .in('area_id', buildingIds)
    .is('deleted_at', null)
    .order('offset_x')

  if (unitsError) throw unitsError

  // Group units by area_id
  const unitsByArea = new Map<string, UnitFor3D[]>()
  for (const unit of units || []) {
    const areaId = unit.area_id
    if (!unitsByArea.has(areaId)) {
      unitsByArea.set(areaId, [])
    }
    unitsByArea.get(areaId)!.push(unit)
  }

  // Combine buildings with their units
  return hangars.map(hangar => ({
    ...(hangar as HangarRow),
    units: unitsByArea.get(hangar.id) || [],
  }))
}
